import { CardColors } from "$components/card";
import { InvestigationIcon as RawInvestigationIcon } from "$components/icons";
import type { EpocusEncounterStatus } from "$global-providers/rxdb-provider/collections/epocus-encounter";
import { examTypeLabel } from "$global-providers/rxdb-provider/collections/epocus-exam-worksheet-labels";
import type {
  AortaWorksheetData,
  BladderWorksheetData,
  CardiacWorksheetData,
  EFastWorksheetData,
  LungWorksheetData,
  RenalWorksheetData,
} from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import {
  CardHeader,
  PatientHistoryCard,
} from "$routes/patient/_lib/history-card";
import { Chip, styled, Typography } from "@material-ui/core";
import { format } from "date-fns";
import type { FC } from "react";
import type { EpocusEncounterWithExams } from "./use-patient-epocus-encounters";

// ─── Styled primitives ────────────────────────────────────────────────────────

const InvestigationIcon = styled(RawInvestigationIcon)(({ theme }) => ({
  marginRight: theme.spacing(1),
  color: theme.palette.blue.main,
}));

const FooterContainer = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(5),
  alignItems: "center",
  padding: theme.spacing(2),
  paddingTop: theme.spacing(0),
  paddingBottom: theme.spacing(1),
  backgroundColor: theme.palette.background.default,
}));

const FooterField = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(0.5),
  alignItems: "center",
  fontSize: 14,
  lineHeight: 1.5,
  whiteSpace: "nowrap",
  fontFamily: theme.typography.fontFamily,
}));

const FooterLabel = styled("span")(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 400,
  fontFamily: theme.typography.fontRegular,
}));

const FooterValue = styled("span")(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 700,
  fontFamily: theme.typography.fontBold,
}));

const ExamSection = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
  marginBottom: theme.spacing(1.5),
}));

const ExamTitle = styled(Typography)(({ theme }) => ({
  fontSize: 13,
  fontWeight: 700,
  color: theme.palette.text.secondary,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
}));

const FindingRow = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(0.5),
  fontSize: 14,
}));

const FindingLabel = styled("span")(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontFamily: theme.typography.fontRegular,
}));

const FindingValue = styled("span")(({ theme }) => ({
  color: theme.palette.text.primary,
  fontFamily: theme.typography.fontBold,
  fontWeight: 700,
}));

const NotesText = styled(Typography)(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.text.secondary,
  fontStyle: "italic",
  marginTop: theme.spacing(0.5),
}));

// ─── Label maps ──────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<EpocusEncounterStatus, string> = {
  imported: "Imported",
  draft: "Draft",
  pending_review: "Pending Review",
  reviewed: "Reviewed",
  complete: "Complete",
};

// ─── Findings summaries (per exam type) ──────────────────────────────────────

type Finding = { label: string; value: string };

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const findingValueLabel = (
  v: "absent" | "present" | "indeterminate" | "not_assessed" | null | undefined,
): string => {
  if (v === null || v === undefined) return "Not assessed";
  return capitalize(v.replace("_", " "));
};

const efastFindings = (w: EFastWorksheetData): Finding[] => {
  const hasFluid = (
    [
      w.findings.ruqFreeFluid,
      w.findings.luqFreeFluid,
      w.findings.pelvicFreeFluid,
    ] as const
  ).some((f) => f === "present");
  const hasPneumo = (
    [w.findings.rPneumothorax, w.findings.lPneumothorax] as const
  ).some((f) => f === "present");
  return [
    { label: "Free fluid", value: hasFluid ? "Present" : "Absent" },
    {
      label: "Pericardial effusion",
      value: findingValueLabel(w.findings.pericardialEffusion),
    },
    { label: "Pneumothorax", value: hasPneumo ? "Present" : "Absent" },
  ];
};

const cardiacFindings = (w: CardiacWorksheetData): Finding[] => {
  const findings: Finding[] = [];
  if (w.findings.lvFunction !== null) {
    const lvLabels = {
      normal: "Normal",
      reduced: "Reduced",
      severely_reduced: "Severely reduced",
    };
    findings.push({
      label: "LV function",
      value: lvLabels[w.findings.lvFunction] ?? "Not assessed",
    });
  }
  if (w.findings.pericardialEffusion !== null) {
    const effLabels = {
      none: "None",
      small: "Small",
      moderate: "Moderate",
      large: "Large",
    };
    findings.push({
      label: "Pericardial effusion",
      value: effLabels[w.findings.pericardialEffusion ?? "none"],
    });
  }
  if (w.findings.rvDilatation !== null) {
    findings.push({
      label: "RV dilatation",
      value: w.findings.rvDilatation ? "Present" : "Absent",
    });
  }
  return findings;
};

const lungFindings = (w: LungWorksheetData): Finding[] => {
  const zones = Object.values(w.zones);
  const hasEffusion = zones.some(
    (z) => z.pleuralEffusion && z.pleuralEffusion !== "none",
  );
  const hasConsolidation = zones.some((z) => z.consolidation === true);
  const hasBLines = zones.some((z) => z.bLines && z.bLines !== "0");
  return [
    { label: "Pleural effusion", value: hasEffusion ? "Present" : "Absent" },
    { label: "Consolidation", value: hasConsolidation ? "Present" : "Absent" },
    { label: "B-lines", value: hasBLines ? "Present" : "Absent" },
  ];
};

const bladderFindings = (w: BladderWorksheetData): Finding[] => {
  const findings: Finding[] = [];
  if (w.findings.bladderVolumeMl !== null) {
    findings.push({
      label: "Bladder volume",
      value: `${w.findings.bladderVolumeMl} mL`,
    });
  }
  if (w.findings.distension !== null) {
    findings.push({
      label: "Distension",
      value: capitalize(w.findings.distension ?? "normal"),
    });
  }
  return findings;
};

const renalFindings = (w: RenalWorksheetData): Finding[] => {
  const hydroLabels = {
    none: "None",
    mild: "Mild",
    moderate: "Moderate",
    severe: "Severe",
  };
  return [
    {
      label: "R hydronephrosis",
      value: hydroLabels[w.findings.rKidney.hydronephrosis ?? "none"],
    },
    {
      label: "L hydronephrosis",
      value: hydroLabels[w.findings.lKidney.hydronephrosis ?? "none"],
    },
    {
      label: "Stones",
      value:
        w.findings.rKidney.stones || w.findings.lKidney.stones
          ? "Present"
          : "Absent",
    },
  ];
};

const aortaFindings = (w: AortaWorksheetData): Finding[] => {
  const hasAneurysm =
    w.findings.proximal.aneurysm ||
    w.findings.mid.aneurysm ||
    w.findings.distal.aneurysm;
  const maxDiameter = Math.max(
    w.findings.proximal.diameterCm ?? 0,
    w.findings.mid.diameterCm ?? 0,
    w.findings.distal.diameterCm ?? 0,
  );
  return [
    { label: "Aneurysm", value: hasAneurysm ? "Present" : "Absent" },
    ...(maxDiameter > 0
      ? [{ label: "Max diameter", value: `${maxDiameter.toFixed(1)} cm` }]
      : []),
  ];
};

const getFindings = (exam: EpocusEncounterWithExams["exams"][0]): Finding[] => {
  const w = exam.worksheetData;
  switch (w.examType) {
    case "efast":
      return efastFindings(w);
    case "cardiac":
      return cardiacFindings(w);
    case "lung":
      return lungFindings(w);
    case "bladder":
      return bladderFindings(w);
    case "renal":
      return renalFindings(w);
    case "aorta":
      return aortaFindings(w);
    case "pregnancy": {
      const f = w.findings;
      const findings: Finding[] = [];
      if (f.estimatedGestationalAgeWeeks !== null) {
        findings.push({
          label: "EGA",
          value: `${f.estimatedGestationalAgeWeeks} wk`,
        });
      }
      if (f.estimatedFetalWeightG !== null) {
        findings.push({ label: "EFW", value: `${f.estimatedFetalWeightG} g` });
      }
      if (f.fetalPresentation) {
        findings.push({ label: "Presentation", value: f.fetalPresentation });
      }
      if (f.cardiacActivity) {
        findings.push({
          label: "Cardiac activity",
          value: f.cardiacActivity.replace(/_/g, " "),
        });
      }
      return findings;
    }
  }
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatusChip: FC<{ status: EpocusEncounterStatus }> = ({ status }) => {
  const colorMap: Partial<
    Record<EpocusEncounterStatus, "default" | "primary" | "secondary">
  > = {
    pending_review: "primary",
    reviewed: "secondary",
    complete: "secondary",
  };
  return (
    <Chip
      label={STATUS_LABELS[status]}
      color={colorMap[status] ?? "default"}
      size="small"
    />
  );
};

const ExamFindings: FC<{ exam: EpocusEncounterWithExams["exams"][0] }> = ({
  exam,
}) => {
  const findings = getFindings(exam);
  const notes = exam.worksheetData.notes;
  return (
    <ExamSection>
      <ExamTitle>{examTypeLabel(exam.examType)}</ExamTitle>
      {findings.map((f) => (
        <FindingRow key={f.label}>
          <FindingLabel>{f.label}:</FindingLabel>
          <FindingValue>{f.value}</FindingValue>
        </FindingRow>
      ))}
      {notes && <NotesText>Notes: {notes}</NotesText>}
    </ExamSection>
  );
};

// ─── Main card ────────────────────────────────────────────────────────────────

export const EpocusHistoryCard: FC<{ encounter: EpocusEncounterWithExams }> = ({
  encounter,
}) => {
  const examTypeNames = encounter.exams
    .map((e) => examTypeLabel(e.examType))
    .join(" + ");

  const title = examTypeNames || "ePocus Scan";

  const Header = () => (
    <CardHeader
      color={CardColors.INVESTIGATION}
      category="ePocus"
      title={title}
      icon={<InvestigationIcon />}
      extras={<StatusChip status={encounter.status} />}
      id={encounter.id}
    />
  );

  const Footer = () => (
    <FooterContainer>
      <FooterField>
        <FooterLabel>Date:</FooterLabel>
        <FooterValue>
          {format(new Date(encounter.date), "d MMM yyyy")}
        </FooterValue>
      </FooterField>
      {encounter.location && (
        <FooterField>
          <FooterLabel>Location:</FooterLabel>
          <FooterValue>{encounter.location}</FooterValue>
        </FooterField>
      )}
    </FooterContainer>
  );

  return (
    <PatientHistoryCard
      id={`epocusCard${encounter.id}`}
      color={CardColors.INVESTIGATION}
      defaultExpanded={false}
      HeaderComponent={Header}
      FooterComponent={Footer}
    >
      {encounter.exams.map((exam) => (
        <ExamFindings key={exam.id} exam={exam} />
      ))}
    </PatientHistoryCard>
  );
};
